import { _lang } from '@umu/ukit-lang';
/**
 * @file copy & refactor from umu_mobile
 */

/**
 * 乘法函数，用来得到精确的乘法结果
 * via: https://gist.github.com/binjoo/3926896#file-gistfile1-js-L74
 */
export function multipleBy(arg1: number, arg2: number) {
    let m = 0;
    const s1 = arg1.toString();
    const s2 = arg2.toString();
    try {
        m += s1.split('.')[1].length;
    } catch (e) {}
    try {
        m += s2.split('.')[1].length;
    } catch (e) {}
    return (Number(s1.replace('.', '')) * Number(s2.replace('.', ''))) / Math.pow(10, m);
}

/**
 * 获取从 min-max 范围内的随机整数，包括min和max
 * 只传递一个参数时，为 1-max 范围内的随机整数
 */
export function randomInRange(min: number, max?: number) {
    if (typeof max === 'undefined') {
        max = min;
        min = 1;
    }

    return Math.floor(Math.random() * (max - min + 1) + min);
}

/**
 * @desc 获取从 m-n 范围内的随机整数，包括m和n, 只传递一个参数时，为 1-m 范围内的随机整数
 * @param {number} m
 * @param {number} n
 */
export function randomRange(m: number, n: number) {
    if (typeof m === 'undefined') {
        return 0;
    }

    if (typeof n === 'undefined') {
        n = m;
        m = 1;
    }

    return Math.floor(Math.random() * (n - m + 1) + m);
}

/**
 * 把一个字符串对应到一个随机数
 */
export function getStringRandom(seed: string, round: number = 10) {
    let sum = 0;
    const charList = seed.split('');
    for (let i = 0, l = charList.length; i < l; i++) {
        sum = charList[i].charCodeAt(0) + sum;
    }

    const ret = sum % round;
    return ret;
}

/**
 * 排序格式化
 */
export function numberOrder(number: number): string {
    let ret: number | string = number;
    if (_lang('EN')) {
        if (number === 1) {
            ret = '1st';
        } else if (number === 2) {
            ret = '2nd';
        } else if (number === 3) {
            ret = '3rd';
        } else {
            ret = ret + 'th';
        }
    }

    return ret + '';
}

/**
 * 格式化为K为单位的形式
 * 如果数字不足1000，直接显示数字，如果数字大于1000，使用xx.xK的形式
 */
export function toKUnit(num: number = 0, decimal: number = 1) {
    if (num <= 1e3) {
        return num;
    }
    const k = (num / 1e3).toFixed(decimal);
    return parseFloat(k) + 'K';
}

/**
 * toThousands
 * 将数字按 千位分隔符 格式化，支持设置格式化后的小数
 * @param  {Number} num [要格式化的数字]
 * @param  {Number} n   [要保留的小数位数，-1时不修改现有数字的小数位数]
 * @param  {Boolen} n   [保留小数位数的时候是否要保留0]
 * @return {String}     []
 */
export function toThousands(num: any, n = 0, keepZero = true) {
    num = Number(num);
    if (isNaN(num)) {
        num = 0;
    }
    // 小数点补位
    if (n !== -1) {
        num = String(num.toFixed(n));
    } else if (num - Math.floor(num) < 0.00001) {
        // 不补位时，检测数据是否有溢出
        num = Math.floor(num);
    }
    // 是否要保留小数点后面的.00
    if (!keepZero) {
        if ((num * 100) % 100 === 0) {
            num = Math.round(num);
        }
    }
    num = num + '';
    const re = /(-?\d+)(\d{3})/;
    while (re.test(num)) {
        num = num.replace(re, '$1,$2');
    }
    return num;
}

/**
 * limitMax
 * 将数字格式化为最大位数，，如999+为3，99,999+为5
 * @param  {Number} num             [要格式化的数字]
 * @param  {Number} maxLength       [位数]
 * @param  {Bool} needToThousands   [是否需要再进行千分位分隔]
 * @return {String}                 []
 */
export function limitMax(num: string | number, maxLength = 0, needToThousands = false) {
    const length = String(num).length;
    let ret = num;
    if (maxLength > 0 && length > maxLength) {
        ret = new Array(maxLength + 1).join('9');
        if (needToThousands) {
            ret = toThousands(ret);
        }
        ret += '+';
    }
    return ret;
}

/**
 * 格式化钱
 * @param num {int} 要格式化的数字
 * @param n  {int} 有效数字
 */
export function formatNumToMoney(num: number, n: number = 2) {
    let ret = String(num.toFixed(n));
    const re = /(-?\d+)(\d{3})/;
    while (re.test(ret)) {
        ret = ret.replace(re, '$1,$2');
    }
    return ret;
}

/**
 * 全角→半角
 * http://snipplr.com/view/44770/javascript/
 */
export function numberZenToHan(str: string = '') {
    return str.replace(/[０-９]/g, (val: string) => {
        return String.fromCharCode(val.charCodeAt(0) - 65248);
    });
}

/**
 * 半角→全角
 * http://snipplr.com/view/44770/javascript/
 */
export function numberHanToZen(str: string = '') {
    return str.replace(/\d/g, (val: string) => {
        return String.fromCharCode(val.charCodeAt(0) + 65248);
    });
}

const charIndex = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
];

/**
 * 字母排序号，A -> Z AA - ZZ AAA- ZZZ ...
 * http://snipplr.com/view/44770/javascript/
 */
export function getCharIndex(input: number): string {
    const len = charIndex.length;
    let remain = 0;
    let frequency = 0;
    if (input >= len) {
        frequency = Math.floor(input / len);
        remain = input % len;
        return getCharIndex(frequency - 1) + charIndex[remain];
    } else {
        return charIndex[input];
    }
}
